import React, { useState, useEffect } from 'react';
import axios from 'axios';

export interface ProjectImage {
    id: number;
    url: string;
    altText: string;
}

interface Props {
    projectId: number;
    images: ProjectImage[];
    onChange: (images: ProjectImage[]) => void;
    onInsertMarkdown?: (markdown: string) => void;
}

const ProjectImageUpload: React.FC<Props> = ({ projectId, images, onChange, onInsertMarkdown }) => {
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);

    const submitImage = async () => {
        if (!file) return;
        setUploading(true);
        try {
            const form = new FormData();
            form.append('file', file);
            const { data } = await axios.post<ProjectImage>(
                `/api/projects/${projectId}/images`,
                form,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            onChange([...images, data]); // update parent state
            setFile(null);
        } finally {
            setUploading(false);
        }
    };

    const deleteImage = async (imageId: number) => {
        await axios.delete(`/api/projects/${projectId}/images/${imageId}`);
        onChange(images.filter(img => img.id !== imageId));
    };

    return (
        <div>
            <div className="flex items-center space-x-4 mb-4">
                <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    className="border border-border rounded px-2 py-1"
                />
                <button
                    onClick={submitImage}
                    disabled={!file || uploading}
                    className="btn-primary"
                >
                    {uploading ? 'Uploading…' : 'Add Image'}
                </button>
            </div>
            <div className="grid grid-cols-3 gap-4">
                {images.map((img) => (
                    <div key={img.id} className="relative group">
                        <img
                            src={process.env.REACT_APP_BACKEND_SERVER_URL + img.url}
                            alt={img.altText || 'Gallery Image'}
                            className="rounded shadow border border-border object-cover w-full h-32"
                        />
                        <button
                            onClick={() => deleteImage(img.id)}
                            className="absolute top-1 right-1 bg-error text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition"
                        >
                            ✕
                        </button>
                        {onInsertMarkdown && (
                            <button
                                onClick={() => onInsertMarkdown(`![${img.altText || 'image'}](${img.url})`)}
                                className="absolute bottom-1 left-1 bg-primary text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition"
                                title="Insert into README"
                            >
                                + README
                            </button>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ProjectImageUpload;
